import { useMemo, useState, type ReactNode } from "react";
import { ChevronLeft, ChevronRight, MapPin, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  apiError,
  createAgendamento,
  createAtividade,
  createCasa,
  patchAgendamento,
  removeAgendamento,
  removeAtividade,
  uploadAgendamentoFoto,
  uploadAtividadeFoto,
  type ManutencaoAgendamento,
  type ManutencaoCasa,
  type ManutencaoColaborador,
  type ManutencaoSnapshot,
  type ManutencaoStatus,
} from "@/services/manutencao";
import {
  Acompanhamento,
  CATEGORIAS,
  CARGOS_EXECUTORES,
  OCORRENCIAS,
  STATUS_LABEL,
  StatusBadge,
  etapaAcompanhamento,
  WEEKDAY_SHORT,
  casaLabel,
  enderecoAgendamento,
  fieldClass,
  formatDay,
  formatWeekLabel,
  isoDate,
  weekDays,
} from "./shared";

type OpTab = "painel" | "servicos" | "livre" | "agendamentos" | "novo";

export function OperacaoPanels({
  tab,
  data,
  reload,
  meuNome,
  executores,
  onGo,
}: {
  tab: OpTab;
  data: ManutencaoSnapshot;
  reload: () => Promise<void>;
  meuNome: string;
  executores: ManutencaoColaborador[];
  onGo: (tab: OpTab) => void;
}) {
  const [weekOffset, setWeekOffset] = useState(0);
  const [serviceDay, setServiceDay] = useState(isoDate(new Date()));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [selected, setSelected] = useState<ManutencaoAgendamento | null>(null);
  const [filStatus, setFilStatus] = useState("");
  const [filTec, setFilTec] = useState("");

  const anchor = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [weekOffset]);
  const days = useMemo(() => weekDays(anchor), [anchor]);
  const today = isoDate(new Date());

  const equipe = executores.filter((c) => CARGOS_EXECUTORES.includes(c.cargo));

  async function run(action: () => Promise<void>) {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      await reload();
    } catch (error) {
      setMsg(apiError(error, "Não foi possível salvar."));
    } finally {
      setBusy(false);
    }
  }

  if (tab === "painel") {
    const daSemana = data.agendamentos.filter((a) => days.includes(a.data));
    const hoje = data.agendamentos.filter((a) => a.data === today);
    const cards = [
      { label: "Esta semana", value: daSemana.length, sub: "agendamentos" },
      { label: "Hoje", value: hoje.length, sub: "atendimentos" },
      { label: "Pendentes", value: data.agendamentos.filter((a) => a.status === "agendado").length, sub: "agendados" },
      { label: "Concluídos", value: daSemana.filter((a) => a.status === "concluido").length, sub: "nesta semana" },
    ];
    return (
      <section className="space-y-4">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {cards.map((card) => (
            <button
              key={card.label}
              type="button"
              onClick={() => onGo("agendamentos")}
              className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{card.label}</p>
              <p className="mt-2 text-3xl font-bold text-brand-700">{card.value}</p>
              <p className="text-sm text-slate-500">{card.sub}</p>
            </button>
          ))}
        </div>
        <WeekBar label={formatWeekLabel(days)} onPrev={() => setWeekOffset((v) => v - 1)} onNext={() => setWeekOffset((v) => v + 1)} onToday={() => setWeekOffset(0)} />
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-7">
          {days.map((day, index) => {
            const items = daSemana.filter((a) => a.data === day).sort((a, b) => a.hora.localeCompare(b.hora));
            return (
              <div key={day} className="rounded-2xl border border-slate-200 bg-white p-3">
                <p className={`text-xs font-bold uppercase ${day === today ? "text-brand-700" : "text-slate-500"}`}>
                  {WEEKDAY_SHORT[index]} {formatDay(day)}
                </p>
                <div className="mt-2 space-y-2">
                  {items.length === 0 && <p className="text-xs text-slate-400">Sem visitas</p>}
                  {items.map((item) => (
                    <button key={item.id} type="button" onClick={() => setSelected(item)} className="w-full rounded-xl bg-slate-50 p-2 text-left">
                      <p className="text-sm font-semibold text-slate-800">{item.hora} · {item.cliente}</p>
                      {(item.casa || item.quadra) && (
                        <p className="truncate text-xs font-medium text-slate-600">{enderecoAgendamento(item.casa, item.quadra)}</p>
                      )}
                      <p className="truncate text-xs text-slate-500">{item.empreendimento || "Sem obra"}</p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {selected && (
          <DetalheApt
            key={selected.id}
            apt={selected}
            data={data}
            equipe={equipe}
            busy={busy}
            msg={msg}
            onClose={() => setSelected(null)}
            onRun={run}
          />
        )}
      </section>
    );
  }

  if (tab === "servicos") {
    const diaServico = days.includes(serviceDay) ? serviceDay : days.includes(today) ? today : days[0];
    const mine = data.agendamentos
      .filter((a) => a.data === diaServico && (a.tecnicos.includes(meuNome) || a.tecnico === meuNome))
      .sort((a, b) => a.hora.localeCompare(b.hora));
    return (
      <section className="space-y-4">
        <WeekBar
          label={formatWeekLabel(days)}
          onPrev={() => setWeekOffset((v) => v - 1)}
          onNext={() => setWeekOffset((v) => v + 1)}
          onToday={() => { setWeekOffset(0); setServiceDay(today); }}
        />
        <div className="grid grid-cols-7 gap-1 rounded-2xl border border-slate-200 bg-white p-2">
          {days.map((day, index) => (
            <button
              key={day}
              type="button"
              onClick={() => setServiceDay(day)}
              className={`min-h-12 rounded-xl px-1 py-2 text-center text-xs font-bold ${diaServico === day ? "bg-brand-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}
            >
              <div>{WEEKDAY_SHORT[index]}</div>
              <div>{formatDay(day).slice(0, 2)}</div>
            </button>
          ))}
        </div>
        <p className="text-sm text-slate-500">
          {mine.length} serviço{mine.length === 1 ? "" : "s"} em {diaServico === today ? "hoje" : formatDay(diaServico)} para {meuNome || "você"}
        </p>
        {msg && <p className="text-sm text-red-600">{msg}</p>}
        {mine.length === 0 && (
          <Empty text="Nenhum serviço atribuído a você neste dia. O nome do usuário precisa coincidir com a equipe do agendamento." />
        )}
        <div className="grid gap-3">
          {mine.map((item) => (
            <button key={item.id} type="button" onClick={() => setSelected(item)} className="rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{item.cliente}</p>
                  <p className="text-sm text-slate-500">{item.hora}</p>
                </div>
                <StatusBadge status={item.status} />
              </div>
              <p className="mt-2 flex items-center gap-1 text-sm text-slate-600">
                <MapPin className="h-4 w-4 shrink-0" /> {enderecoAgendamento(item.casa, item.quadra)} · {item.empreendimento || "—"}
              </p>
              <p className="mt-1 text-sm text-slate-500">{item.problemas.slice(0, 2).join(" · ")}</p>
              <Acompanhamento status={item.status} />
            </button>
          ))}
        </div>
        {selected && <RegistroServico apt={selected} busy={busy} msg={msg} onClose={() => setSelected(null)} onRun={run} />}
      </section>
    );
  }

  if (tab === "livre") {
    return <LivrePanel data={data} meuNome={meuNome} busy={busy} msg={msg} onRun={run} />;
  }

  if (tab === "novo") {
    return <NovoAgendamento data={data} equipe={equipe} busy={busy} msg={msg} onRun={run} onDone={() => onGo("agendamentos")} />;
  }

  const filtrados = data.agendamentos
    .filter((a) => days.includes(a.data))
    .filter((a) => !filStatus || a.status === filStatus)
    .filter((a) => !filTec || a.tecnicos.includes(filTec) || a.tecnico === filTec)
    .sort((a, b) => a.data.localeCompare(b.data) || a.hora.localeCompare(b.hora));

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <WeekBar label={formatWeekLabel(days)} onPrev={() => setWeekOffset((v) => v - 1)} onNext={() => setWeekOffset((v) => v + 1)} onToday={() => setWeekOffset(0)} />
        <Button type="button" onClick={() => onGo("novo")} leftIcon={<Plus className="h-4 w-4" />}>Novo</Button>
      </div>
      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 md:grid-cols-4">
        <label className="text-xs font-semibold uppercase text-slate-500">Status
          <select className={`${fieldClass()} mt-1`} value={filStatus} onChange={(e) => setFilStatus(e.target.value)}>
            <option value="">Todos</option>
            {(["agendado", "atendimento", "concluido", "cancelado", "ausente", "parcial", "reagendado"] as const).map((k) => (
              <option key={k} value={k}>{STATUS_LABEL[k]}</option>
            ))}
          </select>
        </label>
        <label className="text-xs font-semibold uppercase text-slate-500">Técnico
          <select className={`${fieldClass()} mt-1`} value={filTec} onChange={(e) => setFilTec(e.target.value)}>
            <option value="">Todos</option>
            {equipe.map((c) => <option key={c.id} value={c.nome}>{c.nome}</option>)}
          </select>
        </label>
        <p className="self-end text-sm text-slate-500 md:col-span-2">{filtrados.length} agendamento{filtrados.length === 1 ? "" : "s"} nesta semana</p>
      </div>
      {msg && <p className="text-sm text-red-600">{msg}</p>}
      <div className="space-y-2">
        {filtrados.map((item) => (
          <button key={item.id} type="button" onClick={() => setSelected(item)} className="flex w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900">{formatDay(item.data)} · {item.hora} · {item.cliente}</p>
              <p className="text-sm font-medium text-slate-700">{enderecoAgendamento(item.casa, item.quadra)}</p>
              <p className="truncate text-sm text-slate-500">{item.empreendimento || "Sem obra"} · {item.tecnicos.join(", ")}</p>
              <Acompanhamento status={item.status} />
            </div>
            {(etapaAcompanhamento(item.status) == null || item.status === "parcial" || item.status === "reagendado") && (
              <StatusBadge status={item.status} />
            )}
          </button>
        ))}
        {filtrados.length === 0 && <Empty text="Nenhum agendamento neste período." />}
      </div>
      {selected && (
        <DetalheApt
          key={selected.id}
          apt={selected}
          data={data}
          equipe={equipe}
          busy={busy}
          msg={msg}
          onClose={() => setSelected(null)}
          onRun={run}
        />
      )}
    </section>
  );
}

function WeekBar({ label, onPrev, onNext, onToday }: { label: string; onPrev: () => void; onNext: () => void; onToday: () => void }) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2">
      <button type="button" onClick={onPrev} className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-600" aria-label="Semana anterior"><ChevronLeft className="h-4 w-4" /></button>
      <div className="text-center">
        <p className="text-sm font-bold text-brand-700">{label}</p>
        <button type="button" onClick={onToday} className="text-xs font-semibold text-slate-500">Semana atual</button>
      </div>
      <button type="button" onClick={onNext} className="flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 text-slate-600" aria-label="Próxima semana"><ChevronRight className="h-4 w-4" /></button>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-sm text-slate-500">{text}</div>;
}

function DetalheApt({
  apt,
  data,
  equipe,
  busy,
  msg,
  onClose,
  onRun,
}: {
  apt: ManutencaoAgendamento;
  data: ManutencaoSnapshot;
  equipe: ManutencaoColaborador[];
  busy: boolean;
  msg: string | null;
  onClose: () => void;
  onRun: (action: () => Promise<void>) => Promise<void>;
}) {
  const [editando, setEditando] = useState(false);
  const [concluindo, setConcluindo] = useState(false);
  const podeAndamento = apt.status === "agendado" || apt.status === "reagendado";
  const podeConcluir = apt.status === "atendimento" || apt.status === "parcial";

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-3 sm:items-center">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-5 shadow-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-lg font-bold text-slate-900">{apt.cliente}</p>
            <p className="text-sm text-slate-500">{formatDay(apt.data)} às {apt.hora}</p>
          </div>
          <StatusBadge status={apt.status} />
        </div>
        <Acompanhamento status={apt.status} />
        {concluindo ? (
          <RegistroServicoForm
            apt={apt}
            busy={busy}
            msg={msg}
            sugerirConcluido
            onBack={() => setConcluindo(false)}
            onClose={onClose}
            onRun={onRun}
          />
        ) : editando ? (
          <EditarAgendamento
            apt={apt}
            data={data}
            equipe={equipe}
            busy={busy}
            msg={msg}
            onCancel={() => setEditando(false)}
            onRun={onRun}
            onClose={onClose}
          />
        ) : (
          <>
            <dl className="mt-4 space-y-2 text-sm">
              <Row k="Telefone" v={apt.telefone || "—"} />
              <Row k="Endereço" v={enderecoAgendamento(apt.casa, apt.quadra)} />
              <Row k="Obra" v={`${apt.empreendimento || "—"} · ${apt.cidade || "—"}`} />
              <Row k="Equipe" v={apt.tecnicos.join(", ") || "—"} />
              <Row k="Problemas" v={apt.problemas.join(", ")} />
              <Row k="Observação" v={apt.observacao || "Sem observações."} />
              {apt.ocorrencia != null && <Row k="Ocorrência" v={OCORRENCIAS[apt.ocorrencia] || "—"} />}
            </dl>
            {apt.fotos.length > 0 && (
              <div className="mt-4 grid grid-cols-3 gap-2">
                {apt.fotos.map((foto) => (
                  <a key={foto.id} href={foto.url} target="_blank" rel="noreferrer">
                    <img src={foto.url} alt="" className="h-20 w-full rounded-lg object-cover" />
                  </a>
                ))}
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2 sm:flex-nowrap">
              <Button type="button" className="shrink-0 whitespace-nowrap" onClick={() => setEditando(true)}>Editar</Button>
              {podeAndamento && (
                <Button type="button" className="shrink-0 whitespace-nowrap" variant="secondary" loading={busy} onClick={() => void onRun(async () => { await patchAgendamento(apt.id, { status: "atendimento" }); onClose(); })}>Em andamento</Button>
              )}
              {podeConcluir && (
                <Button type="button" className="shrink-0 whitespace-nowrap" variant="secondary" onClick={() => setConcluindo(true)}>Concluir</Button>
              )}
              {apt.status !== "cancelado" && apt.status !== "concluido" && (
                <Button type="button" className="shrink-0 whitespace-nowrap" variant="danger" loading={busy} onClick={() => void onRun(async () => { await patchAgendamento(apt.id, { status: "cancelado" }); onClose(); })}>Cancelar</Button>
              )}
              <Button type="button" className="shrink-0 whitespace-nowrap" variant="ghost" loading={busy} onClick={() => { if (window.confirm("Excluir este agendamento?")) void onRun(async () => { await removeAgendamento(apt.id); onClose(); }); }}>Excluir</Button>
              <Button type="button" className="shrink-0 whitespace-nowrap" variant="ghost" onClick={onClose}>Fechar</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function EditarAgendamento({
  apt,
  data,
  equipe,
  busy,
  msg,
  onCancel,
  onRun,
  onClose,
}: {
  apt: ManutencaoAgendamento;
  data: ManutencaoSnapshot;
  equipe: ManutencaoColaborador[];
  busy: boolean;
  msg: string | null;
  onCancel: () => void;
  onRun: (action: () => Promise<void>) => Promise<void>;
  onClose: () => void;
}) {
  const [cliente, setCliente] = useState(apt.cliente);
  const [telefone, setTelefone] = useState(apt.telefone);
  const [casa, setCasa] = useState(apt.casa);
  const [quadra, setQuadra] = useState(apt.quadra);
  const [problemas, setProblemas] = useState(apt.problemas.filter((item) => item !== "Não classificado"));
  const [observacao, setObservacao] = useState(apt.observacao);
  const [dataVisita, setDataVisita] = useState(apt.data);
  const [hora, setHora] = useState(apt.hora);
  const [tecnicos, setTecnicos] = useState(apt.tecnicos);

  const emp = data.empreendimentos.find((item) => item.nome === apt.empreendimento);
  const casas = emp ? data.casas.filter((item) => item.empreendimentoId === emp.id) : [];
  const itensPadrao = new Set(CATEGORIAS.flatMap((cat) => cat.itens));
  const personalizados = data.problemas.filter((item) => !itensPadrao.has(item.item));
  const catalogo = new Set([...itensPadrao, ...personalizados.map((item) => item.item)]);
  const [avulsos] = useState(() =>
    apt.problemas.filter((item) => item !== "Não classificado" && !catalogo.has(item)),
  );
  const nomesEquipe = new Set(equipe.map((c) => c.nome));
  const tecnicosFora = apt.tecnicos.filter((nome) => !nomesEquipe.has(nome));

  function toggle(list: string[], value: string, set: (next: string[]) => void) {
    set(list.includes(value) ? list.filter((item) => item !== value) : [...list, value]);
  }

  return (
    <form
      className="mt-4 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        void onRun(async () => {
          if (!cliente.trim()) throw new Error("Informe o nome do cliente.");
          if (!dataVisita || !hora) throw new Error("Informe data e horário.");
          if (!tecnicos.length) throw new Error("Selecione pelo menos um funcionário.");
          const mudouAgenda = dataVisita !== apt.data || hora !== apt.hora;
          const voltaAgendado = mudouAgenda && (apt.status === "atendimento" || apt.status === "parcial" || apt.status === "ausente" || apt.status === "reagendado");
          await patchAgendamento(apt.id, {
            cliente: cliente.trim(),
            telefone: telefone.trim(),
            casa: casa.trim(),
            quadra: quadra.trim(),
            problemas,
            observacao,
            data: dataVisita,
            hora,
            tecnicos,
            ...(voltaAgendado ? { status: "agendado" as const } : {}),
          });
          onClose();
        });
      }}
    >
      {msg && <p className="text-sm text-red-600">{msg}</p>}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Cliente"><input className={fieldClass()} value={cliente} onChange={(e) => setCliente(e.target.value)} required /></Field>
        <Field label="Telefone"><input className={fieldClass()} value={telefone} onChange={(e) => setTelefone(e.target.value)} /></Field>
        <Field label="Número da casa"><input className={fieldClass()} value={casa} onChange={(e) => setCasa(e.target.value)} placeholder="Ex.: 12" /></Field>
        <Field label="Quadra / lote"><input className={fieldClass()} value={quadra} onChange={(e) => setQuadra(e.target.value)} placeholder="Ex.: QD162-1A" /></Field>
        <Field label="Data"><input type="date" className={fieldClass()} value={dataVisita} onChange={(e) => setDataVisita(e.target.value)} required /></Field>
        <Field label="Horário"><input type="time" className={fieldClass()} value={hora} onChange={(e) => setHora(e.target.value)} required /></Field>
      </div>
      {casas.length > 0 && (
        <Field label="Preencher pela residência cadastrada">
          <select
            className={fieldClass()}
            defaultValue=""
            onChange={(e) => {
              const item = casas.find((casaItem) => casaItem.id === e.target.value);
              if (!item) return;
              setCasa(item.numero);
              setQuadra(item.quadra);
            }}
          >
            <option value="">Escolha uma casa da lista…</option>
            {casas.map((item) => (
              <option key={item.id} value={item.id}>{casaLabel(item.numero)} · {item.quadra || "sem quadra"}</option>
            ))}
          </select>
        </Field>
      )}
      <Field label="Equipe">
        <div className="max-h-28 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
          {equipe.length === 0 && tecnicosFora.length === 0 && <p className="text-xs text-slate-500">Cadastre a equipe na aba Equipe.</p>}
          {equipe.map((c) => (
            <label key={c.id} className="flex items-center gap-2 text-sm normal-case">
              <input type="checkbox" checked={tecnicos.includes(c.nome)} onChange={() => toggle(tecnicos, c.nome, setTecnicos)} />
              {c.nome}
            </label>
          ))}
          {tecnicosFora.map((nome) => (
            <label key={nome} className="flex items-center gap-2 text-sm normal-case">
              <input type="checkbox" checked={tecnicos.includes(nome)} onChange={() => toggle(tecnicos, nome, setTecnicos)} />
              {nome}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Problemas">
        <div className="max-h-40 space-y-2 overflow-y-auto rounded-lg border border-slate-200 p-2">
          {CATEGORIAS.map((cat) => (
            <div key={cat.nome}>
              <p className="text-[11px] font-bold tracking-wide text-slate-500">{cat.nome}</p>
              {cat.itens.map((item) => (
                <label key={item} className="flex items-center gap-2 py-0.5 text-sm normal-case text-slate-700">
                  <input type="checkbox" checked={problemas.includes(item)} onChange={() => toggle(problemas, item, setProblemas)} />
                  {item}
                </label>
              ))}
            </div>
          ))}
          {personalizados.map((item) => (
            <label key={item.id} className="flex items-center gap-2 py-0.5 text-sm normal-case text-slate-700">
              <input type="checkbox" checked={problemas.includes(item.item)} onChange={() => toggle(problemas, item.item, setProblemas)} />
              {item.categoria} → {item.item}
            </label>
          ))}
          {avulsos.map((item) => (
            <label key={item} className="flex items-center gap-2 py-0.5 text-sm normal-case text-slate-700">
              <input type="checkbox" checked={problemas.includes(item)} onChange={() => toggle(problemas, item, setProblemas)} />
              {item}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Observação">
        <textarea className={fieldClass()} rows={4} maxLength={2000} value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Inclua o que mudou na visita..." />
      </Field>
      {(apt.status === "atendimento" || apt.status === "parcial" || apt.status === "ausente" || apt.status === "reagendado") && (dataVisita !== apt.data || hora !== apt.hora) && (
        <p className="text-sm text-amber-800">A nova data ou horário volta o acompanhamento para Agendado.</p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" loading={busy}>Salvar alterações</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Voltar</Button>
      </div>
    </form>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-1">
      <dt className="shrink-0 text-slate-500">{k}</dt>
      <dd className="min-w-0 flex-1 whitespace-pre-wrap break-words text-right font-medium text-slate-800">{v}</dd>
    </div>
  );
}

function RegistroServico({
  apt,
  busy,
  msg,
  onClose,
  onRun,
}: {
  apt: ManutencaoAgendamento;
  busy: boolean;
  msg: string | null;
  onClose: () => void;
  onRun: (action: () => Promise<void>) => Promise<void>;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-3 sm:items-center">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5 shadow-modal">
        <p className="text-lg font-bold text-slate-900">{apt.cliente}</p>
        <p className="text-sm text-slate-500">{enderecoAgendamento(apt.casa, apt.quadra)} · {apt.empreendimento || "—"}</p>
        <Acompanhamento status={apt.status} />
        <RegistroServicoForm apt={apt} busy={busy} msg={msg} onClose={onClose} onRun={onRun} />
      </div>
    </div>
  );
}

function RegistroServicoForm({
  apt,
  busy,
  msg,
  sugerirConcluido = false,
  onBack,
  onClose,
  onRun,
}: {
  apt: ManutencaoAgendamento;
  busy: boolean;
  msg: string | null;
  sugerirConcluido?: boolean;
  onBack?: () => void;
  onClose: () => void;
  onRun: (action: () => Promise<void>) => Promise<void>;
}) {
  const [status, setStatus] = useState<ManutencaoStatus | "">(
    sugerirConcluido
      ? "concluido"
      : apt.status === "concluido" || apt.status === "parcial" || apt.status === "ausente"
        ? apt.status
        : "",
  );
  const [texto, setTexto] = useState(apt.ocTexto);
  const [files, setFiles] = useState<File[]>([]);

  return (
    <div className="mt-3">
      <div className="flex flex-wrap gap-2">
        {apt.problemas.map((p) => <span key={p} className="rounded-full bg-brand-50 px-2 py-1 text-xs font-semibold text-brand-800">{p}</span>)}
      </div>
      <p className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-600 whitespace-pre-wrap">{apt.observacao || "Sem observações."}</p>
      {msg && <p className="mt-3 text-sm text-red-600">{msg}</p>}
      <label className="mt-4 block text-xs font-semibold uppercase text-slate-500">Resultado da visita
        <select className={`${fieldClass()} mt-1`} value={status} onChange={(e) => setStatus(e.target.value as ManutencaoStatus)}>
          <option value="">Selecione o resultado</option>
          <option value="concluido">Concluído</option>
          <option value="parcial">Parcialmente concluído</option>
          <option value="ausente">Cliente ausente</option>
        </select>
      </label>
      <label className="mt-3 block text-xs font-semibold uppercase text-slate-500">Descrição
        <textarea className={`${fieldClass()} mt-1`} rows={3} maxLength={800} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Descreva o que aconteceu na visita..." />
      </label>
      <label className="mt-3 block text-xs font-semibold uppercase text-slate-500">Fotos do serviço
        <input className="mt-1 block w-full text-sm" type="file" accept="image/*,video/*" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
      </label>
      <div className="mt-4 flex gap-2">
        <Button
          type="button"
          loading={busy}
          onClick={() => void onRun(async () => {
            if (status !== "concluido" && status !== "parcial" && status !== "ausente") {
              throw new Error("Selecione o resultado do serviço.");
            }
            const ocorrencia = status === "concluido" ? 0 : status === "ausente" ? 1 : 2;
            await patchAgendamento(apt.id, { status, ocorrencia, ocTexto: texto });
            for (const file of files) await uploadAgendamentoFoto(apt.id, file, "servico");
            onClose();
          })}
        >
          Salvar registro
        </Button>
        <Button type="button" variant="ghost" onClick={onBack ?? onClose}>{onBack ? "Voltar" : "Fechar"}</Button>
      </div>
    </div>
  );
}

function NovoAgendamento({
  data,
  equipe,
  busy,
  msg,
  onRun,
  onDone,
}: {
  data: ManutencaoSnapshot;
  equipe: ManutencaoColaborador[];
  busy: boolean;
  msg: string | null;
  onRun: (action: () => Promise<void>) => Promise<void>;
  onDone: () => void;
}) {
  const [cliente, setCliente] = useState("");
  const [telefone, setTelefone] = useState("");
  const [empId, setEmpId] = useState("");
  const [casaId, setCasaId] = useState("");
  const [problemas, setProblemas] = useState<string[]>([]);
  const [observacao, setObservacao] = useState("");
  const [dataVisita, setDataVisita] = useState(isoDate(new Date()));
  const [hora, setHora] = useState("08:00");
  const [tecnicos, setTecnicos] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [buscaCasa, setBuscaCasa] = useState("");
  const [casaTexto, setCasaTexto] = useState("");
  const [quadraTexto, setQuadraTexto] = useState("");
  const [novaCasa, setNovaCasa] = useState(false);
  const [numeroCasa, setNumeroCasa] = useState("");
  const [quadraCasa, setQuadraCasa] = useState("");

  const emp = data.empreendimentos.find((e) => e.id === empId);
  const casas = data.casas.filter((c) => c.empreendimentoId === empId);
  const extras = data.problemas;

  function toggle(list: string[], value: string, set: (next: string[]) => void) {
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void onRun(async () => {
          const apt = await createAgendamento({
            cliente,
            telefone,
            casa: casaTexto.trim(),
            quadra: quadraTexto.trim(),
            empreendimento: emp?.nome ?? "",
            cidade: emp?.cidade ?? "",
            problemas,
            observacao,
            data: dataVisita,
            hora,
            tecnicos,
          });
          for (const file of files) await uploadAgendamentoFoto(apt.id, file, "agendamento");
          onDone();
        });
      }}
    >
      {msg && <p className="text-sm text-red-600">{msg}</p>}
      <Card title="Dados do cliente">
        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Nome do cliente"><input className={fieldClass()} value={cliente} onChange={(e) => setCliente(e.target.value)} required /></Field>
          <Field label="Telefone"><input className={fieldClass()} value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="(86) 99999-0000" /></Field>
          <Field label="Centro de custo">
            <select className={fieldClass()} value={empId} onChange={(e) => { setEmpId(e.target.value); setCasaId(""); }}>
              <option value="">Selecione o centro de custo...</option>
              {data.empreendimentos.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
            </select>
          </Field>
          <div className="md:col-span-2">
          <Field label="Residência">
            <div className="grid gap-2 sm:grid-cols-2">
              <input className={fieldClass()} value={casaTexto} onChange={(e) => setCasaTexto(e.target.value)} placeholder="Número da casa" />
              <input className={fieldClass()} value={quadraTexto} onChange={(e) => setQuadraTexto(e.target.value)} placeholder="Quadra / lote" />
            </div>
            <p className="mt-1 text-xs font-normal normal-case text-slate-500">Digite o número e a quadra. Eles aparecem no agendamento mesmo sem a casa estar na lista.</p>
            <input className={`${fieldClass()} mt-2`} value={buscaCasa} onChange={(e) => setBuscaCasa(e.target.value)} placeholder={emp ? "Buscar casa ou lote..." : "Selecione o centro de custo primeiro"} disabled={!emp} />
            <div className="mt-2 max-h-48 overflow-y-auto rounded-lg border border-slate-200">
              {!emp && <p className="px-3 py-4 text-sm text-slate-500">Selecione o centro de custo para ver as casas já cadastradas.</p>}
              {emp && casas.length === 0 && (
                <p className="px-3 py-4 text-sm text-slate-500">
                  {emp.origem === "sienge"
                    ? "Nenhuma casa denominada neste centro. Use os campos de número e quadra acima."
                    : "Nenhuma casa neste centro. Use os campos acima ou cadastre uma abaixo."}
                </p>
              )}
              {gruposCasa(casas, buscaCasa).map((grupo) => (
                <div key={grupo.nome}>
                  <p className="bg-slate-50 px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">{grupo.nome}</p>
                  {grupo.itens.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setCasaId(item.id);
                        setCasaTexto(item.numero);
                        setQuadraTexto(item.quadra);
                      }}
                      className={`flex min-h-11 w-full items-center justify-between px-3 text-left text-sm ${casaId === item.id ? "bg-brand-50 font-semibold text-brand-800" : "hover:bg-slate-50"}`}
                    >
                      <span>{casaLabel(item.numero)}</span>
                      <span className="font-mono text-xs text-slate-500">{item.quadra}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
            {emp && emp.origem !== "sienge" && (
            <button type="button" className="mt-2 text-sm font-semibold text-brand-700" onClick={() => setNovaCasa((v) => !v)}>
              Cadastrar nova casa / quadra
            </button>
            )}
            {novaCasa && emp && emp.origem !== "sienge" && (
              <div className="mt-2 grid gap-2 rounded-xl bg-slate-50 p-3 sm:grid-cols-2">
                <input className={fieldClass()} placeholder="Número da casa" value={numeroCasa} onChange={(e) => setNumeroCasa(e.target.value)} />
                <input className={fieldClass()} placeholder="Quadra / lote" value={quadraCasa} onChange={(e) => setQuadraCasa(e.target.value)} />
                <Button
                  type="button"
                  className="sm:col-span-2"
                  loading={busy}
                  onClick={() => void onRun(async () => {
                    await createCasa(emp.id, numeroCasa, quadraCasa);
                    setCasaTexto(numeroCasa.trim());
                    setQuadraTexto(quadraCasa.trim());
                    setCasaId("");
                    setNumeroCasa("");
                    setQuadraCasa("");
                    setNovaCasa(false);
                  })}
                >
                  Salvar casa
                </Button>
              </div>
            )}
          </Field>
          </div>
          {emp?.cidade ? <Field label="Cidade"><input className={fieldClass()} value={emp.cidade} readOnly /></Field> : null}
        </div>
      </Card>
      <Card title="Classificação do problema">
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {CATEGORIAS.map((cat) => (
            <div key={cat.nome}>
              <p className="mb-2 text-[11px] font-bold tracking-wide text-slate-500">{cat.nome}</p>
              {cat.itens.map((item) => (
                <label key={item} className="flex items-center gap-2 py-1 text-sm text-slate-700">
                  <input type="checkbox" checked={problemas.includes(item)} onChange={() => toggle(problemas, item, setProblemas)} />
                  {item}
                </label>
              ))}
            </div>
          ))}
          {extras.length > 0 && (
            <div>
              <p className="mb-2 text-[11px] font-bold tracking-wide text-slate-500">PERSONALIZADOS</p>
              {extras.map((item) => (
                <label key={item.id} className="flex items-center gap-2 py-1 text-sm text-slate-700">
                  <input type="checkbox" checked={problemas.includes(item.item)} onChange={() => toggle(problemas, item.item, setProblemas)} />
                  {item.categoria} → {item.item}
                </label>
              ))}
            </div>
          )}
        </div>
      </Card>
      <Card title="Observação e fotos">
        <textarea className={fieldClass()} rows={3} maxLength={2000} value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Descreva o problema..." />
        <input className="mt-3 block w-full text-sm" type="file" accept="image/*,video/*" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
      </Card>
      <Card title="Agendamento">
        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Data"><input type="date" className={fieldClass()} value={dataVisita} onChange={(e) => setDataVisita(e.target.value)} required /></Field>
          <Field label="Horário"><input type="time" className={fieldClass()} value={hora} onChange={(e) => setHora(e.target.value)} required /></Field>
          <Field label="Funcionários">
            <div className="max-h-36 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {equipe.length === 0 && <p className="text-xs text-slate-500">Cadastre a equipe na aba Equipe.</p>}
              {equipe.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={tecnicos.includes(c.nome)} onChange={() => toggle(tecnicos, c.nome, setTecnicos)} />
                  {c.nome}
                </label>
              ))}
            </div>
          </Field>
        </div>
      </Card>
      <Button type="submit" loading={busy}>Salvar agendamento</Button>
    </form>
  );
}

function LivrePanel({
  data,
  meuNome,
  busy,
  msg,
  onRun,
}: {
  data: ManutencaoSnapshot;
  meuNome: string;
  busy: boolean;
  msg: string | null;
  onRun: (action: () => Promise<void>) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [observacao, setObservacao] = useState("");
  const [dataAtv, setDataAtv] = useState(isoDate(new Date()));
  const [hora, setHora] = useState(new Date().toTimeString().slice(0, 5));
  const [files, setFiles] = useState<File[]>([]);
  const list = [...data.atividades].sort((a, b) => b.data.localeCompare(a.data) || b.hora.localeCompare(a.hora));

  return (
    <section className="space-y-4">
      <div className="flex justify-end">
        <Button type="button" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setOpen(true)}>Registrar serviço</Button>
      </div>
      {msg && <p className="text-sm text-red-600">{msg}</p>}
      {open && (
        <form
          className="rounded-2xl border border-slate-200 bg-white p-4"
          onSubmit={(e) => {
            e.preventDefault();
            void onRun(async () => {
              const atv = await createAtividade({ oficialNome: meuNome || "Equipe", data: dataAtv, hora, observacao });
              for (const file of files) await uploadAtividadeFoto(atv.id, file);
              setOpen(false);
              setObservacao("");
              setFiles([]);
            });
          }}
        >
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Data"><input type="date" className={fieldClass()} value={dataAtv} onChange={(e) => setDataAtv(e.target.value)} /></Field>
            <Field label="Horário"><input type="time" className={fieldClass()} value={hora} onChange={(e) => setHora(e.target.value)} /></Field>
          </div>
          <Field label="O que foi feito">
            <textarea className={`${fieldClass()} mt-1`} rows={3} value={observacao} onChange={(e) => setObservacao(e.target.value)} required />
          </Field>
          <input className="mt-3 block text-sm" type="file" accept="image/*,video/*" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []))} />
          <div className="mt-3 flex gap-2">
            <Button type="submit" loading={busy}>Salvar</Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
          </div>
        </form>
      )}
      {list.length === 0 && <Empty text="Nenhum serviço sem agendamento registrado." />}
      <div className="grid gap-3">
        {list.map((item) => (
          <article key={item.id} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <span className="rounded-full bg-violet-100 px-2 py-1 text-xs font-semibold text-violet-800">Sem agendamento</span>
              <button type="button" className="text-slate-400 hover:text-red-600" aria-label="Excluir" onClick={() => { if (window.confirm("Excluir este registro?")) void onRun(() => removeAtividade(item.id)); }}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 text-sm text-slate-800">{item.observacao}</p>
            <p className="mt-2 text-xs text-slate-500">{item.oficialNome} · {formatDay(item.data)} · {item.hora} · {item.fotos.length} foto{item.fotos.length === 1 ? "" : "s"}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function gruposCasa(casas: ManutencaoCasa[], busca: string): Array<{ nome: string; itens: ManutencaoCasa[] }> {
  const q = busca.trim().toLowerCase();
  const map = new Map<string, ManutencaoCasa[]>();
  for (const casa of casas) {
    const texto = `${casaLabel(casa.numero)} ${casa.quadra} residência`.toLowerCase();
    if (q && !texto.includes(q)) continue;
    const nome = casa.grupo || "Casas";
    const itens = map.get(nome) ?? [];
    itens.push(casa);
    map.set(nome, itens);
  }
  return [...map.entries()].map(([nome, itens]) => ({ nome, itens }));
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4">
      <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-500">{title}</h3>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500">
      {label}
      <div className="mt-1 normal-case">{children}</div>
    </label>
  );
}
